
import { ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/context/AuthContext";
import { LoadingSpinner3D } from "@/components/LoadingSpinner3D";
import { logWithTimestamp } from "../utils/authUtils";

interface ProtectedRouteProps {
  children: ReactNode;
  redirectPath?: string;
}

export const ProtectedRoute = ({ 
  children, 
  redirectPath = "/auth" 
}: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  
  useEffect(() => {
    // Once the auth context has finished its initial loading, update our local state
    if (!isLoading) {
      logWithTimestamp(`ProtectedRoute: Auth check complete, isAuthenticated=${isAuthenticated}`);
      setIsChecking(false);
    }
  }, [isLoading, isAuthenticated]);

  // Show loading state while auth is initializing
  if (isChecking || isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-black">
        <LoadingSpinner3D />
        <p className="text-white mt-4">Loading your session...</p>
      </div>
    );
  }

  // Redirect to auth page if not authenticated
  if (!isAuthenticated) {
    logWithTimestamp('ProtectedRoute: User not authenticated, redirecting to auth page');
    return <Navigate to={redirectPath} replace state={{ from: location }} />;
  }

  // If authenticated, render the protected content
  logWithTimestamp('ProtectedRoute: User authenticated, rendering protected content');
  return <>{children}</>;
};
